import { computed, ref, shallowRef } from "vue"
import { defineStore } from "pinia"
import type { Schema, SchemaField } from "@auger/core"
import { getAuger } from "@/init/auger"

// Nullish-tolerant for the same reason as the type lookups: callers hold ids
// that may not be there yet, and a miss already answers null.
export const getSchema = (id?: number | null): Schema | null =>
  id == null ? null : useSchemasStore().get(id)
export const getSchemaName = (id?: number | null): string | null => getSchema(id)?.name || null
export const getSchemaFields = (id?: number | null): SchemaField[] => getSchema(id)?.fields || []
export const getSchemasForType = (type?: string | null, includeUntyped = false): Schema[] =>
  type ? useSchemasStore().forType(type, includeUntyped) : []
export const getSchemaByName = (type: string, name: string): Schema | null =>
  useSchemasStore().getByName(type, name)
export const getAllSchemas = (): Schema[] => useSchemasStore().schemas

// A view over @auger/core's catalog, which loads, holds and writes the schemas:
// a schema write through core shows up here with no store patching.
export const useSchemasStore = defineStore("schemas", () => {
  const catalog = getAuger().catalog
  const schemas = shallowRef<Schema[]>(catalog.schemas())
  const loading = ref(false)
  const initialized = ref(catalog.snapshot() != null)
  catalog.onChange(() => {
    schemas.value = catalog.schemas()
    initialized.value = true
  })

  const byId = computed<Record<number, Schema>>(() =>
    Object.fromEntries(schemas.value.map((s) => [s.id, s]))
  )

  const byType = computed<Record<string, Schema[]>>(() => {
    const groups: Record<string, Schema[]> = {}
    schemas.value.forEach((s) => {
      const type = s.record_type || "_untyped"
      if (!groups[type]) groups[type] = []
      groups[type].push(s)
    })
    return groups
  })

  const untyped = computed(() => byType.value._untyped || [])

  const get = (id: number): Schema | null => byId.value[id] || null

  const forType = (type: string, includeUntyped = false): Schema[] => {
    const typed = byType.value[type] || []
    return includeUntyped ? [...typed, ...untyped.value] : typed
  }

  const getByName = (type: string, name: string): Schema | null =>
    forType(type).find((s) => s.name === name) || null

  /** Make the catalog current (see core's `catalog.refresh`). */
  const fetch = () => {
    loading.value = true
    return catalog
      .refresh()
      .then(() => schemas.value)
      .finally(() => {
        loading.value = false
      })
  }

  return {
    schemas,
    loading,
    initialized,
    byId,
    byType,
    untyped,
    get,
    forType,
    getByName,
    fetch,
  }
})
